import { IMAGE_CONFIG } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors, withJsonpSupport } from '@angular/common/http';
import {
  effect,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  getAnalytics,
  provideAnalytics,
  ScreenTrackingService,
  setAnalyticsCollectionEnabled,
  setConsent,
  UserTrackingService,
} from '@angular/fire/analytics';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { connectAuthEmulator, getAuth, provideAuth } from '@angular/fire/auth';
import { connectStorageEmulator, getStorage, provideStorage } from '@angular/fire/storage';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatIconRegistry } from '@angular/material/icon';
import { MAT_PAGINATOR_DEFAULT_OPTIONS, MatPaginatorDefaultOptions } from '@angular/material/paginator';
import { bootstrapApplication, DomSanitizer } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  TitleStrategy,
  withComponentInputBinding,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { firstValueFrom } from 'rxjs';
import { register as registerSwiper } from 'swiper/element';

import { AdvertisementsEffects } from './app/advertisements/advertisements.effects';
import { advertisementsFeature } from './app/advertisements/advertisements.feature';
import { provideApi } from './app/api/provide-api';
import { AppComponent } from './app/app.component';
import { routes } from './app/app-routing';
import { AuthenticationService } from './app/auth/authentication.service';
import { CitiesEffects } from './app/cities/cities.effects';
import { citiesFeature } from './app/cities/cities.feature';
import {
  DEFAULT_LANGUAGE,
  httpLanguageInterceptor,
  LanguageService,
  SUPPORTED_LANGUAGES,
  TranslocoJsLoader,
} from './app/locales';
import { CookieConsentService } from './app/shared/cookie-consent.service';
import { PwaService } from './app/shared/pwa.service';
import { CustomTitleStrategy } from './app/title-strategy';
import { environment } from './environments/environment';

registerSwiper();

function initApplication(
  languageService: LanguageService,
  translocoService: TranslocoService,
  _: AuthenticationService, // it just needs to be loaded so the constructor effects trigger
  iconRegistry: MatIconRegistry,
  sanitizer: DomSanitizer,
) {
  return () => {
    iconRegistry.setDefaultFontSetClass('material-symbols-outlined');
    iconRegistry.addSvgIconResolver((name, namespace) => {
      if (namespace === '') {
        return sanitizer.bypassSecurityTrustResourceUrl(`assets/icons/${name}.svg`);
      }
      console.error(`Unknown icon: ${namespace} ${name}`);
      return null;
    });
    const lang = languageService.language();
    translocoService.setActiveLang(lang);
    const promises = [firstValueFrom(translocoService.load(lang))];
    return Promise.all(promises);
  };
}

bootstrapApplication(AppComponent, {
  providers: [
    provideTransloco({
      config: {
        availableLangs: SUPPORTED_LANGUAGES,
        defaultLang: DEFAULT_LANGUAGE,
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoJsLoader,
    }),
    {
      provide: LOCALE_ID,
      deps: [LanguageService],
      useFactory: (languageService: LanguageService) => languageService.getCurrentLocale(),
    },
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => {
      const auth = getAuth();
      // setPersistence(auth, indexedDBLocalPersistence);
      if (environment.useEmulators) {
        connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
      }
      return auth;
    }),
    provideStorage(() => {
      const storage = getStorage();
      if (environment.useEmulators) {
        connectStorageEmulator(storage, 'localhost', 9199);
      }
      return storage;
    }),
    provideAnalytics(() => {
      const analytics = getAnalytics();
      const consentService = inject(CookieConsentService);
      setAnalyticsCollectionEnabled(analytics, consentService.hasAccepted());
      effect(() => {
        const preference = consentService.preference();
        if (preference === 'accepted') {
          setConsent({
            analytics_storage: 'granted',
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
          });
          setAnalyticsCollectionEnabled(analytics, true);
        } else if (preference === 'denied') {
          setConsent({
            analytics_storage: 'denied',
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
          });
          setAnalyticsCollectionEnabled(analytics, false);
          consentService.clearAnalyticsCookies();
        }
      });
      return analytics;
    }),
    ScreenTrackingService,
    UserTrackingService,
    provideAppInitializer(() => {
      inject(PwaService).init();
      inject(ScreenTrackingService);
      inject(UserTrackingService);
      const initializerFn = initApplication(
        inject(LanguageService),
        inject(TranslocoService),
        inject(AuthenticationService),
        inject(MatIconRegistry),
        inject(DomSanitizer),
      );
      return initializerFn();
    }),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    {
      provide: MAT_PAGINATOR_DEFAULT_OPTIONS,
      useValue: {
        showFirstLastButtons: true,
        pageSizeOptions: [10, 20, 50, 100, 200, 500],
        pageSize: 50,
      } satisfies MatPaginatorDefaultOptions,
    },
    { provide: TitleStrategy, useClass: CustomTitleStrategy },
    provideAnimations(),
    provideRouter(
      routes,
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withComponentInputBinding(),
      withViewTransitions({ skipInitialTransition: true }),
    ),
    provideHttpClient(withFetch(), withInterceptors([httpLanguageInterceptor]), withJsonpSupport()),
    provideStore({
      [citiesFeature.name]: citiesFeature.reducer,
      [advertisementsFeature.name]: advertisementsFeature.reducer,
    }),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode(), connectInZone: true }),
    provideEffects(CitiesEffects, AdvertisementsEffects),
    provideZonelessChangeDetection(),
    { provide: IMAGE_CONFIG, useValue: { disableImageSizeWarning: true, disableImageLazyLoadWarning: true } },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideApi({ basePath: '' }),
  ],
}).catch((err) => console.error(err));
